import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { extractApiMessage } from '@/lib/api';
import { formatoEuros, type CamadaLiquidacionListado } from '@/lib/liquidacion';
import { useCamadasLiquidacion, useLiquidacionEvolucion } from '@/lib/queries/liquidacion';
import { fechaEvolucion } from '@/lib/liquidacion-evolucion';
import { EvolucionAvisos } from '@/components/EvolucionAvisos';

export default function LiquidacionesListado() {
  const { t } = useTranslation();
  const [yearText, setYearText] = useState('');
  const [offset, setOffset] = useState(0);
  const year = yearText ? Number(yearText) : undefined;
  const valid = year === undefined || (Number.isInteger(year) && year >= 2000 && year <= 2100);
  const active = useCamadasLiquidacion(false);
  const history = useCamadasLiquidacion(true, valid ? year : undefined, offset);
  const pending = history.data?.items.filter((c) => !c.liquidacion) ?? [];
  const saved = history.data?.items.filter((c) => c.liquidacion) ?? [];
  function group(title: string, rows: CamadaLiquidacionListado[]) {
    if (!rows.length) return null;
    return (
      <section className="mt-6">
        <h2 className="mb-2 text-base font-semibold">{title}</h2>
        <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
          {rows.map((c) => (
            <li key={c.id}>
              <Link
                to={`/liquidacion/${c.id}`}
                className="block p-4 hover:bg-surface-alt focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              >
                <p className="break-words font-semibold">{c.codigo_lote}</p>
                <p className="mt-1 text-sm text-text-secondary">
                  {t(`pienso_ajustes.${c.sexo}`)} ·{' '}
                  {new Intl.DateTimeFormat('es-ES').format(new Date(`${c.fecha_entrada}T12:00:00`))}
                </p>
                <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">
                    {c.liquidacion?.local_pending
                      ? 'Pendiente de sincronizar'
                      : t(
                          `liquidacion.${c.liquidacion ? 'estado_guardada' : !c.cerrada ? 'estado_simulacion' : 'estado_pendiente'}`,
                        )}
                  </span>
                  {c.liquidacion && (
                    <span className="break-all font-semibold tabular-nums">
                      {formatoEuros(c.liquidacion.total_eur)}
                    </span>
                  )}
                </div>
                {c.liquidacion && (
                  <p className="mt-1 text-xs text-text-secondary">
                    {new Intl.DateTimeFormat('es-ES').format(
                      new Date(c.liquidacion.fecha_guardado),
                    )}
                  </p>
                )}
                <p className="mt-3 text-sm font-medium text-primary">
                  {t(`liquidacion.${c.liquidacion ? 'ver' : c.cerrada ? 'calcular' : 'preparar'}`)}{' '}
                  →
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    );
  }
  return (
    <main className="mx-auto max-w-md px-4 pb-8">
      <h1 className="text-xl font-bold">{t('liquidacion.titulo')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('liquidacion.listado_intro')}</p>
      {active.isLoading && <p className="mt-4">{t('common.loading')}</p>}
      {active.isError && (
        <p role="alert" className="mt-4 text-warning-text">
          {extractApiMessage(active.error)}
        </p>
      )}
      {group(t('liquidacion.camada_activa'), active.data?.items ?? [])}
      {active.data?.items.map((c) => (
        <EstimacionActiva key={c.id} camada={c} />
      ))}
      <div className="mt-6 flex items-end gap-3">
        <label className="block flex-1 text-sm font-medium">
          {t('liquidacion.filtro_year')}
          <input
            type="number"
            min="2000"
            max="2100"
            placeholder={t('liquidacion.todos_years')}
            value={yearText}
            onChange={(e) => {
              setYearText(e.target.value);
              setOffset(0);
            }}
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-surface px-3"
          />
        </label>
        {yearText && (
          <button
            className="min-h-11 px-3 text-sm text-primary"
            onClick={() => {
              setYearText('');
              setOffset(0);
            }}
          >
            {t('liquidacion.limpiar_year')}
          </button>
        )}
      </div>
      {!valid ? (
        <p role="alert" className="mt-3 text-warning-text">
          {t('liquidacion.year_invalido')}
        </p>
      ) : (
        <>
          {history.isLoading && <p className="mt-4">{t('common.loading')}</p>}
          {history.isError && (
            <p role="alert" className="mt-4 text-warning-text">
              {extractApiMessage(history.error)}
            </p>
          )}
          {group(t('liquidacion.pendientes'), pending)}
          {group(t('liquidacion.guardadas'), saved)}
          {history.data?.total === 0 && (
            <p className="mt-6 text-text-secondary">{t('liquidacion.listado_vacio')}</p>
          )}
          {history.data && history.data.total > 25 && (
            <nav
              aria-label={t('liquidacion.paginas')}
              className="mt-5 flex items-center justify-between gap-2"
            >
              <button
                disabled={offset === 0}
                onClick={() => setOffset(offset - 25)}
                className="min-h-11 px-2 disabled:opacity-40"
              >
                {t('liquidacion.anterior')}
              </button>
              <span className="text-sm text-text-secondary">
                {offset + 1}–{Math.min(offset + 25, history.data.total)} / {history.data.total}
              </span>
              <button
                disabled={offset + 25 >= history.data.total}
                onClick={() => setOffset(offset + 25)}
                className="min-h-11 px-2 disabled:opacity-40"
              >
                {t('liquidacion.siguiente')}
              </button>
            </nav>
          )}
        </>
      )}
      <Link
        to="/ajustes/liquidacion"
        className="mt-6 inline-flex min-h-11 items-center text-sm text-primary"
      >
        {t('liquidacion.ajustes')}
      </Link>
    </main>
  );
}

function EstimacionActiva({ camada }: { camada: CamadaLiquidacionListado }) {
  const { t } = useTranslation();
  const q = useLiquidacionEvolucion(camada.id);
  const dia = q.data?.serie.at(-1);
  return (
    <section
      aria-label={t('evolucion.escenario')}
      className="mt-3 space-y-3 border-b border-border pb-4"
    >
      {q.isError ? (
        <p role="alert" className="text-sm text-warning-text">
          {extractApiMessage(q.error)}
        </p>
      ) : q.isFetching || !dia ? (
        <p role="status" className="text-sm">
          {t('liquidacion.calculando')}
        </p>
      ) : (
        <>
          <p className="text-sm">
            {t('evolucion.tarjeta_fecha', { fecha: fechaEvolucion(dia.fecha) })}:{' '}
            <strong className="tabular-nums">
              {dia.resultado ? formatoEuros(dia.resultado.total_eur) : t('evolucion.pendiente')}
            </strong>
          </p>
          <EvolucionAvisos dia={dia} />
        </>
      )}
      <p className="text-sm text-text-secondary">{t('evolucion.tarjeta_aviso')}</p>
      <Link
        to={`/liquidacion/${camada.id}/evolucion`}
        className="inline-flex min-h-11 items-center text-sm font-medium text-accent-icon"
      >
        {t('evolucion.ver_evolucion')}
      </Link>
    </section>
  );
}
